import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Button, Checkbox, Chip, CircularProgress, Menu, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Tooltip } from '@mui/material';
// import { format } from 'date-fns';
import React, { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { SHARED_APP_LIST_EDIT_URL, SHARED_APP_LIST_NEW_URL } from '../../constants/routeUrls';
import { useLabelEntries, useLabelTypes } from '../../hooks/useLabels';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';
import { useSharedAppLists } from '../../hooks/useSharedAppLists';
import { getComparator, stableSort } from '../../util/tableUtils';
import LabelManagement from './LabelManagement';

interface SharedAppListProps {
  selectable?: boolean;
  selectedIds?: number[];
  onSelectionChange?: (id: number, selected: boolean) => void;
  listTypeFilter?: string;
}

function SharedAppList({ 
  selectable = false, 
  selectedIds = [], 
  onSelectionChange,
  listTypeFilter
}: SharedAppListProps) {
  const [tempNameFilter, setTempNameFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const navigate = useNavigate();

  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewMyRecent, setViewMyRecent] = useState(false);
  
  const { addRecentItem, isRecentlyViewed } = useRecentlyViewed('sharedAppLists-recent');
  
  // Label filtering state
  const [labelFilters, setLabelFilters] = useState<{[key: string]: string[]}>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentLabelType, setCurrentLabelType] = useState<string | null>(null);
  
  // Fetch label types and entries
  const { data: labelTypesData, isLoading: isLoadingLabelTypes } = useLabelTypes();
  const { data: labelEntriesData } = useLabelEntries();

  const { 
    data: sharedAppData, 
    isLoading,
  } = useSharedAppLists(nameFilter, page + 1, rowsPerPage, labelFilters);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setNameFilter(tempNameFilter);
      setPage(0);
    }
  };

  /*
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };
  */

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // Apply client-side filters that can't be done server-side
  const applyClientSideFilters = (lists: any[]) => {
    if (!lists) return [];
    
    let filteredLists = lists;
    
    // Apply "My Recent" filter client-side since it's user-specific
    if (viewMyRecent) {
      filteredLists = lists.filter(list => isRecentlyViewed(list.id));
    }
    
    // Apply list type filter client-side for the selector modal
    if (listTypeFilter) {
      filteredLists = filteredLists.filter(list => list.list_type === listTypeFilter);
    }
    
    return filteredLists;
  };
    
  const clientFilteredLists = applyClientSideFilters(sharedAppData?.collection || []);

  const sortedLists = React.useMemo(() => {
    if (!clientFilteredLists || clientFilteredLists.length === 0) return [];
    return stableSort(clientFilteredLists as any[], getComparator(order, orderBy as keyof any));
  }, [clientFilteredLists, order, orderBy]);
  
  // Label filter handlers
  const handleLabelTypeClick = (event: React.MouseEvent<HTMLElement>, type: string) => {
    setCurrentLabelType(type);
    setAnchorEl(event.currentTarget);
  };
  
  const handleLabelEntrySelect = (entry: string, type?: string) => {
    const labelType = type || currentLabelType;
    if (!labelType) return;
    
    setLabelFilters(prev => {
      const currentEntries = prev[labelType] || [];
      const newEntries = currentEntries.includes(entry)
        ? currentEntries.filter(e => e !== entry) // Remove if already selected
        : [...currentEntries, entry]; // Add if not selected
      
      const newFilters = {
        ...prev,
        [labelType]: newEntries
      };
      
      // Remove empty arrays
      if (newEntries.length === 0) {
        delete newFilters[labelType];
      }
      
      return newFilters;
    });
    
    // Reset to first page when filters change
    setPage(0);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setCurrentLabelType(null);
  };
  
  const clearAllFilters = () => {
    setLabelFilters({});
    setViewMyRecent(false);
    // Reset to first page when clearing filters
    setPage(0);
  };
  
  const isLabelEntrySelected = (type: string, entry: string) => (labelFilters[type] || []).includes(entry);

  return (
    <Stack p={selectable ? 0 : 2} spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <Stack spacing={1} direction="row" alignItems="center" sx={{ flexGrow: 1, overflowX: 'auto' }}> 
        <Button
            variant={viewMyRecent ? "contained" : "outlined"}
            onClick={() => {
              setViewMyRecent(!viewMyRecent);
              setPage(0);
            }}
          >
            {viewMyRecent ? "My Recent View Active" : "View My Recent"}
          </Button>
          
          {/* Label filter buttons */}
          {isLoadingLabelTypes ? (
            <CircularProgress size={24} />
          ) : (
            labelTypesData?.collection?.map((labelType: any) => (
              <Button
                key={labelType.id}
                variant={labelFilters[labelType.labelType] ? "contained" : "outlined"}
                onClick={(e) => handleLabelTypeClick(e, labelType.labelType)}
                sx={{ minWidth: 'auto' }}
              >
                {labelType.labelType}
              </Button>
            ))
          )}
          
        </Stack>
        
        {/* Label entries menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          {currentLabelType && labelEntriesData?.collection
            ?.filter((entry: any) => entry.labelTypeName === currentLabelType)
            .map((entry: any) => (
              <MenuItem 
                key={entry.id}
                onClick={() => handleLabelEntrySelect(entry.entry)}
                selected={isLabelEntrySelected(currentLabelType, entry.entry)}
              >
                {entry.entry}
              </MenuItem>
            ))
          }
          {currentLabelType && (!labelEntriesData?.collection?.some((entry: any) => 
            entry.labelTypeName === currentLabelType) || labelEntriesData?.collection?.length === 0) && (
            <MenuItem disabled>No entries found</MenuItem>
          )}
        </Menu>

        <Stack spacing={1} direction="row" alignItems="center">
          <Autocomplete
            freeSolo
            value={tempNameFilter}
            options={[]}
            size="small"
            style={{ width: 160 }}
            onChange={(e, value, reason) => {
              if (reason === 'clear') {
                setNameFilter('');
                setPage(0);
              }
            }}
            clearIcon={isLoading ? <CircularProgress color="inherit" size={16} /> : undefined}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                variant="outlined"
                size="small"
                placeholder="Search"
                onKeyDown={handleKeyPress}
                onChange={(e) => setTempNameFilter(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon color="action" fontSize="small" />
                }}
              />
            )}
          />
          
          {!selectable && (
            <Stack direction="row" spacing={1}>
              <LabelManagement />
              <Button 
                onClick={() => navigate(SHARED_APP_LIST_NEW_URL)} 
                variant="contained"
                startIcon={<AddIcon />}
              >
                New Shared App List
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
      
      {/* Active filters row */}
      {Object.keys(labelFilters).length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Stack direction="row" spacing={1} sx={{ flexGrow: 1, flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(labelFilters).map(([type, entries]) => 
              entries.map(entry => (
                <Button 
                  key={`${type}-${entry}`}
                  variant="contained"
                  color="primary"
                  onClick={() => handleLabelEntrySelect(entry, type)}
                  endIcon={<CloseIcon />}
                >
                  {`${type}: ${entry}`}
                </Button>
              ))
            )}
          </Stack>
          
          <Button 
            variant="outlined" 
            color="error"
            onClick={clearAllFilters}
          >
            Clear Filters
          </Button>
        </Stack>
      )}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {selectable && <TableCell width={50} />}
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleRequestSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'list_type'}
                  direction={orderBy === 'list_type' ? order : 'asc'}
                  onClick={() => handleRequestSort('list_type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'targeting_profiles'}
                  direction={orderBy === 'targeting_profiles' ? order : 'asc'}
                  onClick={() => handleRequestSort('targeting_profiles')}
                >
                  Ad Groups
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'labels'}
                  direction={orderBy === 'labels' ? order : 'asc'}
                  onClick={() => handleRequestSort('labels')}
                >
                  Labels
                </TableSortLabel>
              </TableCell>
              {/* <TableCell>
                <TableSortLabel
                  active={orderBy === 'updated_at'}
                  direction={orderBy === 'updated_at' ? order : 'asc'}
                  onClick={() => handleRequestSort('updated_at')}
                >
                  Last Updated
                </TableSortLabel>
              </TableCell> */}
              {/* <TableCell>Active</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* eslint-disable-next-line no-nested-ternary */}
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={selectable ? 8 : 7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (sortedLists && sortedLists.length > 0 ? (
              sortedLists.map((list) => (
                <TableRow 
                  key={list.id} 
                  hover 
                  onClick={selectable 
                    ? () => onSelectionChange?.(list.id, !selectedIds.includes(list.id))
                    : () => {
                        addRecentItem(list.id, list.name);
                        const editUrl = generatePath(SHARED_APP_LIST_EDIT_URL, { id: list.id.toString() });
                        navigate(editUrl);
                      }
                  }
                  style={{ cursor: 'pointer' }}
                  selected={selectable && selectedIds.includes(list.id)}
                >
                  {selectable && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox 
                        checked={selectedIds.includes(list.id)} 
                        onChange={(e) => onSelectionChange?.(list.id, e.target.checked)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{list.id}</TableCell>
                  <TableCell>{list.name}</TableCell>
                  <TableCell>{list.list_type}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {list.targeting_profiles && list.targeting_profiles.length > 0 ? (
                      <Tooltip 
                        title={
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(10, 1fr)', 
                            gap: '8px',
                            maxWidth: '600px',
                            padding: '4px'
                          }}>
                            {[...list.targeting_profiles]
                              .sort((a, b) => b - a)
                              .map((id) => (
                                <div key={id} style={{ textAlign: 'center' }}>
                                  {id}
                                </div>
                              ))}
                          </div>
                        }
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: '620px',
                              backgroundColor: 'rgba(97, 97, 97, 0.92)',
                              pointerEvents: 'auto'
                            }
                          }
                        }}
                      >
                        <Chip 
                          label={`${list.targeting_profiles.length} Ad Group${list.targeting_profiles.length !== 1 ? 's' : ''}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      'None'
                    )}
                  </TableCell>
                  <TableCell>
                    {list.labels && list.labels.length > 0 ? (
                      list.labels.map(label => `${label.type}: ${label.entry}`).join(', ')
                    ) : (
                      'None'
                    )}
                  </TableCell>
                  {/* <TableCell>{formatDate(list.updated_at)}</TableCell> */}
                  {/* <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox 
                      checked={list.is_active || false} 
                      disabled 
                    />
                  </TableCell> */}
                </TableRow>
              ))
            ) :
              <TableRow>
                <TableCell colSpan={selectable ? 8 : 7} align="center">No shared app lists found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!selectable && sharedAppData?.pagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={sharedAppData.pagination.total_items || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </Stack>
  );
}

export default SharedAppList; 